import type { Icon } from "@studio3t/generic-components";
import type { QueryClient } from "@tanstack/react-query";
import { schemasApi, servicesApi } from "@/api/client";
import { queryKeys } from "@/hooks/queryKeys";
import type { SchemaEntrySummary } from "@/types/api";
import { schemaCapableServices } from "@/utils/schemaServices";
import { type StagedResource, schemaRowId } from "./stagingModel";

// The stored schema a dropped stage reuses. Introspection draws a random
// sample, so re-introspecting a collection larger than the sample never
// reproduces the schema saved from an earlier drop — numeric bounds, enum
// members, required sets, formats and even the variant split move with
// the sample. Comparing a fresh seed against the store therefore never
// matches, and the row would star as "unsaved" forever. A schema already
// saved under the collection's name is staged as-is instead; the
// introspected draft is only for collections the store has no schema for.

/** The stored entry carrying the collection's name as a stageable
 *  resource, or null. The store allows several entries per name; the
 *  first listed wins — the pick the rows' already-saved check makes. */
export function storedSchemaFor(
  serviceName: string,
  entries: SchemaEntrySummary[],
  name: string,
  icon: Icon,
): StagedResource | null {
  const match = entries.find((entry) => entry.name === name);
  if (!match) return null;
  return { id: schemaRowId(serviceName, match.id), name, icon };
}

// The icon lib is DOM-bound (styled-components), and the staging modules
// this feeds are exercised by DOM-less unit tests — so the schema glyph is
// loaded lazily on the async path rather than imported at module load.
async function schemaIcon(): Promise<Icon> {
  const { Icon } = await import("@studio3t/generic-components");
  return Icon.Schema;
}

// The order the stores are searched in: the dropped stage's own service
// first (the natural home of a schema named after its collection), then
// the other schema-capable services in list order.
function searchOrder(
  services: { name: string; provider: string }[],
  preferredService: string | undefined,
): string[] {
  const names = schemaCapableServices(services).map((s) => s.name);
  if (!preferredService || !names.includes(preferredService)) return names;
  return [preferredService, ...names.filter((n) => n !== preferredService)];
}

/** How long a drop waits for the schema stores to answer before the slot
 *  seeds from introspection. A Mongo service that is down takes ~30 s to
 *  fail its list (server selection timeout); the drop must not. A store
 *  still silent at the deadline counts as holding nothing for this drop
 *  — the user can still drag its schema from the explorer once it lists. */
export const STORE_LOOKUP_BUDGET_MS = 5_000;

/** The first non-null result in search order — the preferred store wins
 *  over a faster one — or null once every lookup settled empty or the
 *  budget ran out on a silent one. */
export async function firstInOrder<T>(
  lookups: Promise<T | null>[],
  budgetMs: number,
): Promise<T | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), budgetMs);
  });
  try {
    for (const lookup of lookups) {
      const result = await Promise.race([lookup, deadline]);
      if (result !== null) return result;
    }
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/** A store the explorer already shows as unreachable: its list has failed
 *  and never once answered (Retry on its folder). Read off the error
 *  count, not the status — a refetch of a data-less query (the explorer
 *  refetches stale lists on remount) flips the status back to pending
 *  while the store is still just as dead. The first successful list
 *  clears it. */
function storeFailed(queryClient: QueryClient, serviceName: string): boolean {
  const state = queryClient.getQueryState(queryKeys.schemas(serviceName));
  return (
    state !== undefined &&
    state.data === undefined &&
    state.errorUpdateCount > 0
  );
}

/** The stored schema named after the collection, through the shared
 *  react-query cache (the explorer's service and schema lists). Schemas
 *  live per service (TTTL-319): the dropped stage's service is searched
 *  first, then every other schema-capable one. Every store is asked at
 *  once; one the explorer already found dead is skipped, and one that
 *  stays silent past the budget is given up on, so a single unreachable
 *  service never holds the drop's schema back. A failed lookup reads as
 *  "nothing stored" so the drop falls back to introspection rather than
 *  seeding nothing. */
export async function findStoredSchema(
  queryClient: QueryClient,
  name: string,
  preferredService?: string,
  budgetMs = STORE_LOOKUP_BUDGET_MS,
): Promise<StagedResource | null> {
  let services: { name: string; provider: string }[];
  try {
    services = await queryClient.ensureQueryData({
      queryKey: queryKeys.services({}),
      queryFn: () => servicesApi.list({}),
    });
  } catch {
    return null;
  }
  const icon = await schemaIcon();
  const lookups = searchOrder(services, preferredService)
    .filter((serviceName) => !storeFailed(queryClient, serviceName))
    .map((serviceName) =>
      queryClient
        .ensureQueryData({
          queryKey: queryKeys.schemas(serviceName),
          queryFn: () => schemasApi.list(serviceName),
        })
        .then(
          (entries) => storedSchemaFor(serviceName, entries, name, icon),
          // A store that can't be listed holds nothing to reuse.
          () => null,
        ),
    );
  return firstInOrder(lookups, budgetMs);
}
